import { createEarlyAccessReservation } from '@features/booking/api/reservations';
import type { EarlyAccessGrabPayload } from '@features/booking/api/reservations';
import type { BaseReservationFields, CommitContext, CommitDerived } from './shared';
import { persistSuccessAndAdvance, assertReservationId } from './shared';

/**
 * Early-access reservation path — request-booking endpoint, no payment.
 *
 * Used when booking.isEarlyAccessFlow is true. The early-access flow always
 * uses type 'traditional' with fee 0; payment fields are never read.
 *
 * Guarantee type passed to fireConversion is 'early_access' (special-cased,
 * not from booking.guaranteeType) — see plan §payment-branches §1.4.
 */
export async function commitEarlyAccess(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
): Promise<void> {
  // Narrow date/menu to scalars for the non-WL early-access endpoint.
  // basePayload.date is `string` in this branch (isWaitingList === false) but
  // TS sees the union — the cast pins the runtime fact.
  const earlyAccessPayload: EarlyAccessGrabPayload = {
    ...basePayload,
    date: basePayload.date as string,
    menu: basePayload.menu as number,
    type: 'traditional',
    fee: 0,
  };

  const response = await createEarlyAccessReservation(ctx.currentLanguage, earlyAccessPayload);

  const reservationId = assertReservationId(response.reservationId, {
    endpoint: 'request-booking',
    isWaitingList: derived.isWaitingList,
    flowType: ctx.booking.flowType,
    guaranteeType: 'early_access',
    restaurant: ctx.restaurant.id,
  });

  persistSuccessAndAdvance(
    ctx,
    derived,
    reservationId,
    response.uniqueId,
    'early_access',
  );
}
